import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ShieldCheck, Scale, FileText } from "lucide-react";
import { MaskedLine, FadeUp, Marquee, EASE } from "@/components/motion";
import VipSubscribeForm from "@/components/VipSubscribeForm";
import HeroScoreCard from "@/components/HeroScoreCard";
import ServiceCard from "@/components/ServiceCard";
import VipCountdown from "@/components/VipCountdown";

const TICKER = ["6–60 MO TERMS", "3-BUREAU MONITORING", "VETTED PROVIDERS", "FREE TO COMPARE"];

const SERVICES = [
  { n: "01", title: "Personal Loan", desc: "Request $1,000 to $50,000 and compare personalized offers from vetted lenders in minutes — any credit profile welcome.", to: "/personal-loan", cta: "Get personalized offers", icon: "banknote", stat: "$50K", statLabel: "Max request" },
  { n: "02", title: "Credit Health", desc: "Real-time monitoring, dark-web surveillance and insurance-backed restoration to keep your score and identity safe.", to: "/identity-protection", cta: "Check credit health", icon: "shield", stat: "24/7", statLabel: "Monitoring" },
  { n: "03", title: "Credit Builder", desc: "Builder accounts, dispute automation and starter cards that report to all three bureaus every single month.", to: "/credit-builder", cta: "Compare builders", icon: "trend", stat: "3", statLabel: "Bureaus reported" },
];

const CHAPTERS = [
  { n: "01", title: "Everything in one place", desc: "Lending, credit building and identity protection compared side by side. No juggling five platforms to manage one financial life." },
  { n: "02", title: "Providers we vet, not just list", desc: "Every provider on Trusted Financial Hub passes a review of pricing clarity, reporting practices and customer outcomes before earning a place." },
  { n: "03", title: "Security as a default setting", desc: "Your details travel encrypted, are never sold, and are only shared with the providers needed to fulfil your request." },
  { n: "04", title: "Humans on the other side", desc: "Questions don't disappear into a ticket void. A real support team answers within one business day — usually much faster." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-28 pb-16 overflow-hidden" data-testid="hero-section">
        <div className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center">
          <div>
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-8">
            <MaskedLine delay={0.1}>Trusted Financial Hub — Compare · Choose · Grow</MaskedLine>
          </p>
          <h1 className="font-display font-light text-5xl md:text-6xl tracking-tight leading-[1.02] max-w-2xl text-[#0F1F1A]">
            <MaskedLine delay={0.25}>Borrow with clarity.</MaskedLine>
            <MaskedLine delay={0.45}>Build with <span className="italic text-[#0C6B4F]">confidence.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-[#5C6B62] leading-relaxed"
          >
            Trusted Financial Hub is an independent comparison service that matches you with vetted providers across personal lending, credit building and identity protection — in minutes, without affecting your credit score.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/#apply" data-testid="hero-cta-primary" className="bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-[#0A5C43] active:scale-[0.98] transition-[background-color,transform] duration-300">
              Compare My Options
            </Link>
            <Link to="/how-it-works" data-testid="hero-cta-secondary" className="border border-[#0F1F1A]/20 text-[#0F1F1A] text-xs font-semibold uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:border-[#0C6B4F] hover:text-[#0C6B4F] active:scale-[0.98] transition-[border-color,color,transform] duration-300">
              How It Works
            </Link>
          </motion.div>
          </div>
          <HeroScoreCard />
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
          className="relative z-10 max-w-7xl mx-auto w-full mt-20 border-t border-[#DFE5DC] pt-6 flex flex-wrap gap-x-10 gap-y-3 items-center"
        >
          {TICKER.map((t) => (
            <span key={t} className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#5C6B62]">{t}</span>
          ))}
          <ArrowDown className="w-4 h-4 text-[#0C6B4F] ml-auto animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={["Personal Lending", "Credit Building", "Identity Protection", "Vetted Providers", "Independent Comparison"]} />

      {/* SERVICES BENTO */}
      <section className="px-6 py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-4">What we compare</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-16 max-w-2xl text-[#0F1F1A]">Three doors, one financial future.</h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6" style={{ transformPerspective: 1200 }}>
            {SERVICES.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.1} className="h-full">
                <ServiceCard service={s} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="px-6 py-24 md:py-32 border-t border-[#DFE5DC] bg-white" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-[1fr_2fr] gap-16">
          <div className="md:sticky md:top-28 self-start">
            <FadeUp>
              <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-4">The Trusted Standard</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight text-[#0F1F1A]">Why people choose us.</h2>
            </FadeUp>
          </div>
          <div>
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.05} className="border-t border-[#DFE5DC] py-10 grid grid-cols-[auto_1fr] gap-6 md:gap-10">
                <span className="font-mono2 text-sm text-[#C9A227] pt-2">{c.n}</span>
                <div>
                  <h3 className="font-display text-2xl md:text-3xl mb-3 text-[#0F1F1A]">{c.title}</h3>
                  <p className="text-[#5C6B62] leading-relaxed text-sm md:text-base">{c.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-t border-[#DFE5DC] px-6 py-14" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-10">
          {[
            { icon: ShieldCheck, title: "Data Protected", body: "256-bit encryption on every request. Your information is never sold." },
            { icon: Scale, title: "Independent", body: "Comparisons follow published editorial standards — see our disclosures." },
            { icon: FileText, title: "Fully Transparent", body: "Clear terms, clear pricing, and a public advertising disclosure." },
          ].map((t, i) => (
            <FadeUp key={t.title} delay={i * 0.08} className="flex gap-5 items-start">
              <t.icon className="w-7 h-7 text-[#0C6B4F] shrink-0" strokeWidth={1.5} />
              <div>
                <h4 className="font-display text-xl text-[#0F1F1A] mb-1">{t.title}</h4>
                <p className="text-[#5C6B62] text-sm leading-relaxed">{t.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* VIP LIST */}
      <section id="apply" className="px-6 py-24 md:py-32 border-t border-[#DFE5DC] bg-[#0F3D2E] relative overflow-hidden" data-testid="vip-section">
        <div className="absolute -right-40 -top-40 w-[480px] h-[480px] rounded-full border border-white/10 pointer-events-none" />
        <div className="absolute -right-20 -top-20 w-[300px] h-[300px] rounded-full border border-[#D9B84A]/20 pointer-events-none" />
        <div className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full border border-white/10 pointer-events-none" />
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center relative z-10">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#D9B84A] mb-4">The VIP List</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-6 text-white">
              Priority access. <span className="italic text-[#D9B84A]">Members first.</span>
            </h2>
            <p className="text-white/70 leading-relaxed max-w-md mb-10">
              Join the VIP subscriber list for first access to new provider offers, rate drops and credit-building launches — before they open to the public. Free to join, leave anytime.
            </p>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Next enrollment window opens in</p>
            <VipCountdown />
            <div className="mt-10 flex flex-col gap-3 font-mono2 text-xs uppercase tracking-[0.2em] text-white/50">
              <span>Early access to new offers</span>
              <span>Rate-drop alerts by email</span>
              <span>No hard inquiry, ever</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="bg-white rounded-3xl p-7 md:p-8 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.5)]">
              <VipSubscribeForm />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
